/**
 * MagazineManagement.js
 * Admin settings for pricing, current issues, and uploading sample PDF.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { vendorService } from '../../../services/api';
import {
  Settings, RefreshCw, Upload, Check, Loader, AlertCircle, ChevronDown, Eye, Download, Trash2
} from 'lucide-react';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MagazineManagement = ({ toast }) => {
  const pdfInputRef = useRef(null);

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfDeleting, setPdfDeleting] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const getMediaUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const base = isLocal ? 'http://localhost:8080' : '';
    return `${base}${url}`;
  };

  const handleDeletePdf = async () => {
    if (!window.confirm('Are you sure you want to delete the sample PDF?')) return;
    setPdfDeleting(true);
    try {
      const res = await vendorService.deleteAdminMagazineSamplePdf();
      if (res.success) {
        toast.success('Sample magazine PDF deleted successfully!');
        if (config) {
          setConfig({ ...config, sample_pdf_url: null });
        }
      } else {
        toast.error(res.error || 'Failed to delete sample PDF.');
      }
    } catch {
      toast.error('Failed to delete sample PDF.');
    } finally {
      setPdfDeleting(false);
    }
  };
  const [form, setForm] = useState({
    yearly_price: 699,
    copy_price: 399,
    recent_month: 'June',
    recent_year: 2026,
    is_active: true,
    yearly_page_id: '',
    copy_page_id: '',
    yearly_plan_id: '',
  });

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vendorService.getAdminMagazineConfig();
      if (res.success && res.data) {
        setConfig(res.data);
        setForm({
          yearly_price: res.data.yearly_price,
          copy_price: res.data.copy_price,
          recent_month: res.data.recent_month,
          recent_year: res.data.recent_year,
          is_active: res.data.is_active,
          yearly_page_id: res.data.yearly_page_id || '',
          copy_page_id: res.data.copy_page_id || '',
          yearly_plan_id: res.data.yearly_plan_id || '',
        });
      } else {
        toast.error(res.error || 'Failed to load magazine config.');
      }
    } catch {
      toast.error('Failed to load magazine config.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    setSavedOk(false);
    try {
      const res = await vendorService.updateAdminMagazineConfig(form);
      if (res.success) {
        toast.success('Magazine config updated successfully!');
        setSavedOk(true);
        setConfig(res.data?.config || config);
        setTimeout(() => setSavedOk(false), 3000);
      } else {
        toast.error(res.error || 'Failed to update config.');
      }
    } catch {
      toast.error('Failed to update config.');
    } finally {
      setSaving(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF documents are allowed.');
      return;
    }

    const MAX_SIZE = 50 * 1024 * 1024; // 50 MB
    if (file.size > MAX_SIZE) {
      toast.error('Maximum size limit is 50 MB. Please select a smaller file.');
      return;
    }

    setPdfUploading(true);
    try {
      const res = await vendorService.uploadAdminMagazineSamplePdf(file);
      if (res.success) {
        toast.success('Sample magazine PDF uploaded successfully!');
        if (config) {
          setConfig({ ...config, sample_pdf_url: res.sample_pdf_url });
        }
      } else {
        toast.error(res.error || 'Failed to upload PDF.');
      }
    } catch {
      toast.error('Failed to upload PDF.');
    } finally {
      setPdfUploading(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <RefreshCw className="animate-spin text-[#2ab391]" size={30} />
        <span className="text-sm font-semibold text-gray-500">Loading Configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
          <Settings size={18} className="text-[#2ab391]" />
          Magazine Configuration
        </h3>
        <p className="text-xs text-gray-400 mt-1 font-semibold">Set pricing, default issues and upload sample reader copies.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
        {/* Settings form */}
        <div className="bg-white rounded-xl border border-gray-200/80 p-5 shadow-xs text-left space-y-4">
          <h4 className="text-sm font-bold text-gray-700 border-b border-gray-100 pb-2 mb-4">Pricing & Details</h4>

          {/* Yearly price */}
          <div>
            <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
              Yearly Subscription Price (₹ INR)
            </label>
            <input
              type="number"
              min={0}
              value={form.yearly_price}
              onChange={(e) => setForm((f) => ({ ...f, yearly_price: Number(e.target.value) }))}
              className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#2ab391] transition-all"
            />
          </div>

          {/* Copy price */}
          <div>
            <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
              Recent Issue Copy Price (₹ INR)
            </label>
            <input
              type="number"
              min={0}
              value={form.copy_price}
              onChange={(e) => setForm((f) => ({ ...f, copy_price: Number(e.target.value) }))}
              className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#2ab391] transition-all"
            />
          </div>

          {/* Recent Month */}
          <div>
            <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
              Recent Magazine Month
            </label>
            <div className="relative">
              <select
                value={form.recent_month}
                onChange={(e) => setForm((f) => ({ ...f, recent_month: e.target.value }))}
                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#2ab391] appearance-none cursor-pointer transition-all"
              >
                {MONTHS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Recent Year */}
          <div>
            <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
              Recent Magazine Year
            </label>
            <input
              type="number"
              min={2020}
              max={2099}
              value={form.recent_year}
              onChange={(e) => setForm((f) => ({ ...f, recent_year: Number(e.target.value) }))}
              className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#2ab391] transition-all"
            />
          </div>

          {/* Yearly AutoPay Plan ID */}
          <div>
            <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
              Razorpay AutoPay Plan ID (plan_...)
            </label>
            <input
              type="text"
              value={form.yearly_plan_id}
              onChange={(e) => setForm((f) => ({ ...f, yearly_plan_id: e.target.value }))}
              placeholder="e.g. plan_TZVFvLt9XfHZ3Z"
              className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#2ab391] transition-all"
            />
          </div>

          {/* Yearly Page ID */}
          <div>
            <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
              Razorpay Yearly Subscription Page ID (pl_...)
            </label>
            <input
              type="text"
              value={form.yearly_page_id}
              onChange={(e) => setForm((f) => ({ ...f, yearly_page_id: e.target.value }))}
              placeholder="e.g. pl_SvqcBZm0pqNVY2"
              className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#2ab391] transition-all"
            />
          </div>

          {/* Copy Page ID */}
          <div>
            <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
              Razorpay Single Copy Page ID (pl_...)
            </label>
            <input
              type="text"
              value={form.copy_page_id}
              onChange={(e) => setForm((f) => ({ ...f, copy_page_id: e.target.value }))}
              placeholder="e.g. pl_T0GeNU97R5K01D"
              className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#2ab391] transition-all"
            />
          </div>

          {/* Save button */}
          <button
            onClick={() => setShowConfirmModal(true)}
            disabled={saving}
            className={`mt-6 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-colors active:scale-[0.98] shadow-md shadow-[#2ab391]/10 cursor-pointer ${
              savedOk
                ? 'bg-emerald-500 text-white'
                : 'bg-[#2ab391] hover:bg-[#23977a] text-white disabled:opacity-60'
            }`}
          >
            {saving ? <Loader size={15} className="animate-spin" /> : savedOk ? <Check size={15} /> : null}
            {saving ? 'Saving...' : savedOk ? 'Saved Config!' : 'Save Config'}
          </button>
        </div>

        {/* Sample issue PDF section */}
        <div className="bg-white rounded-xl border border-gray-200/80 p-5 shadow-xs text-left space-y-5">
          <h4 className="text-sm font-bold text-gray-700 border-b border-gray-100 pb-2 mb-4">Sample Reader PDF</h4>

          <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-200 rounded-xl text-center">
            <Upload size={32} className="text-gray-300 mb-2" />
            <p className="text-xs font-bold text-gray-700 mb-1">Click to upload the sample edition PDF</p>
            <p className="text-[10px] text-gray-400">PDF formats only (Max 50MB)</p>

            <input
              type="file"
              ref={pdfInputRef}
              onChange={handlePdfUpload}
              accept=".pdf"
              className="hidden"
            />

            <button
              onClick={() => pdfInputRef.current?.click()}
              disabled={pdfUploading}
              className="mt-4 px-4 py-2 bg-gray-50 border border-gray-200 hover:bg-gray-100 text-gray-700 text-xs font-bold rounded-lg cursor-pointer transition-colors disabled:opacity-60 flex items-center gap-1.5"
            >
              {pdfUploading ? <Loader size={12} className="animate-spin" /> : null}
              {pdfUploading ? 'Uploading...' : 'Choose File'}
            </button>
          </div>

          <div className="bg-gray-50/50 rounded-xl p-3 border border-gray-150 flex items-start gap-2.5">
            <AlertCircle size={15} className="text-[#2ab391] shrink-0 mt-0.5" />
            <div className="w-full">
              <p className="text-xs font-bold text-gray-800">Current Sample Link:</p>
              <p className="text-[10px] font-mono text-gray-500 break-all mt-1">
                {config?.sample_pdf_url ? config.sample_pdf_url : 'No PDF uploaded yet.'}
              </p>
              {config?.sample_pdf_url && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <a
                    href={getMediaUrl(config.sample_pdf_url)}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-[11px] font-bold rounded-lg cursor-pointer transition-colors shadow-xs"
                  >
                    <Eye size={12} className="text-gray-500" />
                    View
                  </a>
                  <button
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const response = await fetch(getMediaUrl(config.sample_pdf_url));
                        if (!response.ok) throw new Error('File not found');
                        const blob = await response.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.download = 'TII_Magazine_Sample.pdf';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(blobUrl);
                      } catch (err) {
                        console.error('Download failed:', err);
                        toast.error('Failed to download PDF.');
                      }
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-[11px] font-bold rounded-lg cursor-pointer transition-colors shadow-xs"
                  >
                    <Download size={12} className="text-gray-500" />
                    Download
                  </button>
                  <button
                    onClick={handleDeletePdf}
                    disabled={pdfDeleting}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 text-[11px] font-bold rounded-lg cursor-pointer transition-colors disabled:opacity-60 shadow-xs ml-auto"
                  >
                    {pdfDeleting ? <Loader size={12} className="animate-spin text-red-500" /> : <Trash2 size={12} />}
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 text-left space-y-4 transform transition-all">
            <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
              <div className="w-10 h-10 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0">
                <AlertCircle className="text-amber-600" size={20} />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Confirm Configuration Update</h3>
                <p className="text-xs text-gray-500 font-medium">Please review the details before saving.</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-3.5 space-y-2 text-xs border border-gray-150">
              <div className="flex justify-between border-b border-gray-200/60 pb-1.5">
                <span className="text-gray-500 font-semibold">Yearly Subscription Price:</span>
                <span className="font-bold text-gray-900">₹{form.yearly_price}</span>
              </div>
              <div className="flex justify-between border-b border-gray-200/60 pb-1.5">
                <span className="text-gray-500 font-semibold">Recent Issue Copy Price:</span>
                <span className="font-bold text-gray-900">₹{form.copy_price}</span>
              </div>
              <div className="flex justify-between border-b border-gray-200/60 pb-1.5">
                <span className="text-gray-500 font-semibold">Recent Issue:</span>
                <span className="font-bold text-gray-900">{form.recent_month} {form.recent_year}</span>
              </div>
              <div className="flex justify-between border-b border-gray-200/60 pb-1.5">
                <span className="text-gray-500 font-semibold">AutoPay Plan ID:</span>
                <span className="font-mono font-bold text-gray-900 break-all">{form.yearly_plan_id || 'Not set (Fallback to .env)'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 font-semibold">Yearly Page ID:</span>
                <span className="font-mono text-gray-700">{form.yearly_page_id || 'N/A'}</span>
              </div>
            </div>

            <p className="text-xs text-gray-600 font-medium leading-relaxed">
              Are you sure you want to save these updated magazine configuration settings?
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-xl transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  handleSave();
                }}
                className="px-4 py-2 bg-[#2ab391] hover:bg-[#23977a] text-white text-xs font-bold rounded-xl transition-colors shadow-xs cursor-pointer"
              >
                Confirm & Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MagazineManagement;
